import { useState } from "react";
import { trpc } from "@/lib/trpc";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Switch } from "@/components/ui/switch";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { formatDate } from "@/lib/dateUtils";
import { Film, Plus, Pencil, Trash2 } from "lucide-react";

type Props = {
  customerId: number;
  /** Customer card Wallet Toggle — independent of wallet row existence. */
  walletEnabled?: boolean;
};

type TxForm = {
  type: "add_money" | "spend_money";
  amount: string;
  transactionDate: string;
  remarks: string;
};

const emptyTxForm = (): TxForm => ({
  type: "add_money",
  amount: "",
  transactionDate: new Date().toISOString().slice(0, 10),
  remarks: "",
});

function formatUsd(amount: string | null | undefined): string {
  const n = parseFloat(amount ?? "0");
  return `US$${Number.isFinite(n) ? n.toFixed(2) : "0.00"}`;
}

export function KaleidescapeWalletStaffPanel({ customerId, walletEnabled = false }: Props) {
  const { data, isLoading, refetch } = trpc.kaleidescapeWallet.staffGetByCustomer.useQuery({ customerId });
  const utils = trpc.useUtils();

  const setWalletEnabled = trpc.customers.setKaleidescapeWalletEnabled.useMutation({
    onSuccess: (res) => {
      toast.success(
        res.kaleidescapeWalletEnabled
          ? "Kaleidescape Wallet visible to customer"
          : "Kaleidescape Wallet hidden from customer",
      );
      void utils.customers.getById.invalidate({ id: customerId });
    },
    onError: (err) => toast.error(err.message),
  });

  const [showCreate, setShowCreate] = useState(false);
  const [createBalance, setCreateBalance] = useState("");
  const [createCardLast4, setCreateCardLast4] = useState("");
  const [createNotes, setCreateNotes] = useState("");

  const [showTxDialog, setShowTxDialog] = useState(false);
  const [editingTxId, setEditingTxId] = useState<number | null>(null);
  const [txForm, setTxForm] = useState<TxForm>(emptyTxForm());

  const [editCardLast4, setEditCardLast4] = useState("");
  const [editInternalNotes, setEditInternalNotes] = useState("");
  const [editingWalletMeta, setEditingWalletMeta] = useState(false);

  const createWallet = trpc.kaleidescapeWallet.staffCreateWallet.useMutation({
    onSuccess: () => {
      toast.success("Kaleidescape Wallet created");
      setShowCreate(false);
      void refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateWallet = trpc.kaleidescapeWallet.staffUpdateWallet.useMutation({
    onSuccess: () => {
      toast.success("Wallet updated");
      setEditingWalletMeta(false);
      void refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const addTransaction = trpc.kaleidescapeWallet.staffAddTransaction.useMutation({
    onSuccess: () => {
      toast.success("Transaction added");
      setShowTxDialog(false);
      setTxForm(emptyTxForm());
      void refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateTransaction = trpc.kaleidescapeWallet.staffUpdateTransaction.useMutation({
    onSuccess: () => {
      toast.success("Transaction updated");
      setShowTxDialog(false);
      setEditingTxId(null);
      setTxForm(emptyTxForm());
      void refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const deleteTransaction = trpc.kaleidescapeWallet.staffSoftDeleteTransaction.useMutation({
    onSuccess: () => {
      toast.success("Transaction removed");
      void refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const wallet = data?.wallet;
  const transactions = data?.transactions ?? [];

  const openAddTx = () => {
    setEditingTxId(null);
    setTxForm(emptyTxForm());
    setShowTxDialog(true);
  };

  const openEditTx = (tx: (typeof transactions)[0]) => {
    setEditingTxId(tx.id);
    setTxForm({
      type: tx.type,
      amount: tx.amount ?? "",
      transactionDate: tx.transactionDate,
      remarks: tx.remarks ?? "",
    });
    setShowTxDialog(true);
  };

  const submitTx = () => {
    if (!wallet) return;
    if (editingTxId) {
      updateTransaction.mutate({
        transactionId: editingTxId,
        walletId: wallet.id,
        ...txForm,
        remarks: txForm.remarks || null,
      });
    } else {
      addTransaction.mutate({
        walletId: wallet.id,
        ...txForm,
        remarks: txForm.remarks || null,
      });
    }
  };

  const startEditWalletMeta = () => {
    if (!wallet) return;
    setEditCardLast4(wallet.cardLast4 ?? "");
    setEditInternalNotes(wallet.internalNotes ?? "");
    setEditingWalletMeta(true);
  };

  return (
    <Card>
      <CardHeader className="pb-3">
        <div className="flex items-center justify-between gap-2">
          <CardTitle className="text-base flex items-center gap-2">
            <Film className="h-4 w-4 text-[#C8A96A]" />
            Kaleidescape Wallet
          </CardTitle>
          {wallet && (
            <Button size="sm" variant="outline" onClick={openAddTx}>
              <Plus className="h-3.5 w-3.5 mr-1" />
              + Add Transaction
            </Button>
          )}
        </div>
      </CardHeader>
      <CardContent>
        <div className="flex items-center justify-between gap-4 mb-4 pb-4 border-b border-border/60">
          <div className="space-y-1">
            <Label htmlFor="kaleidescape-wallet-enabled" className="text-sm font-medium">
              Wallet Toggle
            </Label>
            <p className="text-xs text-muted-foreground leading-relaxed">
              When off, the customer cannot see Wallet navigation, cards, or APIs in Clarity One — even if a
              wallet record exists.
            </p>
          </div>
          <Switch
            id="kaleidescape-wallet-enabled"
            checked={walletEnabled}
            disabled={setWalletEnabled.isPending}
            onCheckedChange={(checked) =>
              setWalletEnabled.mutate({ customerId, enabled: checked })
            }
          />
        </div>
        {isLoading ? (
          <p className="text-sm text-muted-foreground">Loading wallet…</p>
        ) : !wallet ? (
          <div className="space-y-3">
            <p className="text-sm text-muted-foreground">No Kaleidescape Wallet for this customer.</p>
            <Button size="sm" onClick={() => setShowCreate(true)}>
              + Add Kaleidescape Wallet
            </Button>
          </div>
        ) : (
          <div className="space-y-4">
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm">
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Current balance</p>
                <p className="font-semibold text-lg">{formatUsd(wallet.currentBalance)}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Card ending</p>
                <p className="font-medium">{wallet.cardLast4 ? `•••• ${wallet.cardLast4}` : "—"}</p>
              </div>
              <div>
                <p className="text-xs text-muted-foreground uppercase tracking-wide">Last updated</p>
                <p className="font-medium">{wallet.lastUpdatedAt ? formatDate(wallet.lastUpdatedAt) : "—"}</p>
              </div>
              <div className="flex items-end">
                <Button size="sm" variant="ghost" className="h-8 px-2" onClick={startEditWalletMeta}>
                  <Pencil className="h-3.5 w-3.5 mr-1" />
                  Edit details
                </Button>
              </div>
            </div>

            {wallet.internalNotes && !editingWalletMeta && (
              <div className="bg-muted/50 rounded-md p-3 text-sm">
                <p className="text-xs text-muted-foreground uppercase tracking-wide mb-1">Internal notes</p>
                <p className="whitespace-pre-wrap">{wallet.internalNotes}</p>
              </div>
            )}

            {editingWalletMeta && (
              <div className="border rounded-md p-3 space-y-3">
                <div className="grid grid-cols-2 gap-3">
                  <div className="space-y-1">
                    <Label className="text-xs">Card last 4 digits</Label>
                    <Input
                      value={editCardLast4}
                      onChange={(e) => setEditCardLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                      placeholder="1234"
                      maxLength={4}
                      className="h-8"
                    />
                  </div>
                </div>
                <div className="space-y-1">
                  <Label className="text-xs">Internal notes</Label>
                  <Textarea
                    value={editInternalNotes}
                    onChange={(e) => setEditInternalNotes(e.target.value)}
                    className="min-h-[80px] text-sm"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    size="sm"
                    disabled={updateWallet.isPending}
                    onClick={() =>
                      updateWallet.mutate({
                        walletId: wallet.id,
                        cardLast4: editCardLast4 || null,
                        internalNotes: editInternalNotes || null,
                      })
                    }
                  >
                    Save
                  </Button>
                  <Button size="sm" variant="outline" onClick={() => setEditingWalletMeta(false)}>
                    Cancel
                  </Button>
                </div>
              </div>
            )}

            <div>
              <p className="text-xs font-semibold text-muted-foreground uppercase tracking-wide mb-2">
                Transactions
              </p>
              {transactions.length === 0 ? (
                <p className="text-sm text-muted-foreground">No transactions yet.</p>
              ) : (
                <div className="border rounded-md overflow-hidden">
                  <table className="w-full text-sm">
                    <thead>
                      <tr className="bg-muted/40 text-left">
                        <th className="px-3 py-2 font-medium">Date</th>
                        <th className="px-3 py-2 font-medium">Type</th>
                        <th className="px-3 py-2 font-medium text-right">Amount</th>
                        <th className="px-3 py-2 font-medium">Remarks</th>
                        <th className="px-3 py-2 font-medium">Added</th>
                        <th className="px-3 py-2 w-20" />
                      </tr>
                    </thead>
                    <tbody>
                      {transactions.map((tx) => (
                        <tr key={tx.id} className="border-t">
                          <td className="px-3 py-2 whitespace-nowrap">{formatDate(tx.transactionDate)}</td>
                          <td className="px-3 py-2 capitalize">{tx.type.replace("_", " ")}</td>
                          <td className="px-3 py-2 text-right font-medium">{formatUsd(tx.amount)}</td>
                          <td className="px-3 py-2 text-muted-foreground">{tx.remarks || "—"}</td>
                          <td className="px-3 py-2 text-xs text-muted-foreground whitespace-nowrap">
                            {tx.createdAt ? formatDate(tx.createdAt) : "—"}
                          </td>
                          <td className="px-3 py-2">
                            <div className="flex gap-1 justify-end">
                              <button
                                type="button"
                                className="p-1 text-muted-foreground hover:text-foreground"
                                onClick={() => openEditTx(tx)}
                              >
                                <Pencil className="h-3.5 w-3.5" />
                              </button>
                              <button
                                type="button"
                                className="p-1 text-muted-foreground hover:text-red-500"
                                onClick={() => {
                                  if (confirm("Remove this transaction? Balance will be recalculated.")) {
                                    deleteTransaction.mutate({ transactionId: tx.id, walletId: wallet.id });
                                  }
                                }}
                              >
                                <Trash2 className="h-3.5 w-3.5" />
                              </button>
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              )}
            </div>
          </div>
        )}
      </CardContent>

      {/* Create wallet dialog */}
      <Dialog open={showCreate} onOpenChange={setShowCreate}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Add Kaleidescape Wallet</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Current balance (USD)</Label>
              <Input
                value={createBalance}
                onChange={(e) => setCreateBalance(e.target.value)}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label>Card last 4 digits</Label>
              <Input
                value={createCardLast4}
                onChange={(e) => setCreateCardLast4(e.target.value.replace(/\D/g, "").slice(0, 4))}
                placeholder="1234"
                maxLength={4}
              />
            </div>
            <div className="space-y-1">
              <Label>Internal notes</Label>
              <Textarea
                value={createNotes}
                onChange={(e) => setCreateNotes(e.target.value)}
                placeholder="Staff-only notes…"
              />
            </div>
            <Button
              className="w-full"
              disabled={createWallet.isPending || !createBalance}
              onClick={() =>
                createWallet.mutate({
                  customerId,
                  currentBalance: createBalance,
                  cardLast4: createCardLast4 || null,
                  internalNotes: createNotes || null,
                })
              }
            >
              Create wallet
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Transaction dialog */}
      <Dialog open={showTxDialog} onOpenChange={setShowTxDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>{editingTxId ? "Edit transaction" : "Add transaction"}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="space-y-1">
              <Label>Type</Label>
              <select
                value={txForm.type}
                onChange={(e) => setTxForm((f) => ({ ...f, type: e.target.value as TxForm["type"] }))}
                className="w-full border border-input rounded-md px-3 py-2 text-sm bg-background"
              >
                <option value="add_money">Add money</option>
                <option value="spend_money">Spend money</option>
              </select>
            </div>
            <div className="space-y-1">
              <Label>Date</Label>
              <Input
                type="date"
                value={txForm.transactionDate}
                onChange={(e) => setTxForm((f) => ({ ...f, transactionDate: e.target.value }))}
              />
            </div>
            <div className="space-y-1">
              <Label>Amount (USD)</Label>
              <Input
                value={txForm.amount}
                onChange={(e) => setTxForm((f) => ({ ...f, amount: e.target.value }))}
                placeholder="0.00"
              />
            </div>
            <div className="space-y-1">
              <Label>Remarks</Label>
              <Input
                value={txForm.remarks}
                onChange={(e) => setTxForm((f) => ({ ...f, remarks: e.target.value }))}
                placeholder="e.g. Top Up via PayNow"
              />
            </div>
            <Button
              className="w-full"
              disabled={
                (addTransaction.isPending || updateTransaction.isPending) ||
                !txForm.amount ||
                !txForm.transactionDate
              }
              onClick={submitTx}
            >
              {editingTxId ? "Save changes" : "Add transaction"}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </Card>
  );
}
